/* eslint-disable */
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  User,
  Building,
  Phone,
  Globe,
  Search,
  Upload,
  ArrowRight,
  Loader2,
  X,
  LocateIcon
} from "lucide-react";
import { Loader, Toggle } from "rsuite";
import 'rsuite/Loader/styles/index.css';
import 'rsuite/Toggle/styles/index.css';
import { FaLocationDot, FaLocationPin } from "react-icons/fa6";
import { MdDialpad, MdLocationPin } from "react-icons/md";
import SelectOptions from "./common/SelectOptions";
import api from "../services/api";

const UserDetails = () => {
  const modalRef = useRef(null);
  const user_id = localStorage.getItem('user_id');
  const navigate = useNavigate();
  const [mainLoading, setMainLoading] = useState(true);
  const [miniLoading, setMiniLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [browsing, setBrowsing] = useState(false);
  const [searchType, setSearchType] = useState('tollfree');
  const [localitySearch, setLocalitySearch] = useState('');
  const [patternSearch, setPatternSearch] = useState('');
  const [company_id, setCompany_id] = useState('');
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    businessName: "",
    phoneNumber: "",
    country: "US",
    selectedNumber: "",
    portNumber: false,
    website: "",
  });
  const [availableNumbers, setAvailableNumbers] = useState([]);

  useEffect(() => {
    fetchNumbers();
  }, [formData.country, localitySearch, patternSearch, searchType]);

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const fetchNumbers = async () => {
    let data = {
      type: searchType,
      pattern: patternSearch,
      region: formData.country
    }
    if (searchType === 'local') {
      data.area_code = localitySearch;
    }
    setMiniLoading(true);
    const response = await api.fetchNumber(user_id, data);
    if (response.status === 'success') {
      if (mainLoading && formData.selectedNumber === '') {
        setFormData(prev => ({ ...prev, selectedNumber: response.data?.data[0].phoneNumber }));
      }
      setAvailableNumbers(response.data?.data);
    }
    setMiniLoading(false);
  }

  const fetchUserDetails = async () => {
    const response = await api.getUserDetails(user_id);
    if (response.status === 'success') {
      if (response?.data?.account_id) {
        localStorage.setItem('account_id', response.data?.account_id);
        setCompany_id(response.data?.company_id);
        const formdata = {
          first_name: response.data?.first_name || '',
          last_name: response.data?.last_name || '',
          businessName: response.data?.company_name || '',
          phoneNumber: response.data?.mobile || '',
          country: response.data?.region?.toUpperCase() || 'US',
          website: response.data?.website || '',
          selectedNumber: response.data?.company_number,
          portNumber: response.data?.should_host || false,
        }
        setSearchType(response.data?.type === 'toll_free' ? 'tollfree' : 'local');
        setFormData(formdata);
      } else {
        const formdata = {
          first_name: response.data?.first_name || '',
          last_name: response.data?.last_name || '',
          phoneNumber: response.data?.mobile || '',
        }
        setFormData(prev => ({ ...prev, first_name: formdata?.first_name, last_name: formdata?.last_name, phoneNumber: formdata?.phoneNumber }));
      }
    } else {
      console.log(response);
      toast.error(response.message);
    }
    setMainLoading(false);
  }

  const regionOptions = [
    { label: "United States", value: "US" },
    { label: "United Kingdom", value: "GB" },
    { label: "Australia", value: "AU" },
    { label: "Canada", value: "CA" }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Format phone number if the input is for phoneNumber
    if (name === "phoneNumber") {
      const formattedNumber = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedNumber }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleWebsiteBlur = (e) => {
    const value = e.target.value;
    const formattedWebsite = formatWebsiteURL(value);
    setFormData(prev => ({ ...prev, website: formattedWebsite }));
    
    // Validate the formatted URL and show feedback
    if (formattedWebsite && !validateWebsiteURL(formattedWebsite)) {
      toast.error("Please enter a valid website URL");
    }
  };

  const formatWebsiteURL = (url) => {
    if (!url) return url;
    
    // Remove any leading/trailing whitespace for processing
    const cleanUrl = url.trim();
    
    // If already has protocol, return as is
    if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
      return cleanUrl;
    }
    
    // Prevent double protocol prefixes
    if (cleanUrl.startsWith('://')) {
      return `https${cleanUrl}`;
    }
    
    // Add https:// prefix
    return `https://${cleanUrl}`;
  };

  const validateWebsiteURL = (url) => {
    if (!url) return false;
    const urlRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/;
    return urlRegex.test(url);
  };

  const handleSelectChange = (e) => {
    setFormData(prev => ({ ...prev, country: e.target.value }));
  };

  const handleBrowseNumbers = () => {
    setBrowsing(true);
  };

  const handleSelectNumber = (number) => {
    setFormData(prev => ({ ...prev, selectedNumber: number }));
    setBrowsing(false);
    toast.success(`Number ${number} selected!`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate phone number before submission
    if (!validatePhoneNumber(formData.phoneNumber)) {
      toast.error("Please enter a valid phone number based on your selected region.");
      return;
    }

    // Validate website URL before submission
    if (!validateWebsiteURL(formData.website)) {
      toast.error("Please enter a valid website URL (e.g. https://example.com)");
      return;
    }

    if (!formData.first_name || !formData.last_name || !formData.phoneNumber) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      let formdata = {
        user_first_name: formData.first_name,
        user_last_name: formData.last_name,
        name: formData.businessName,
        user_number: formData.phoneNumber,
        region: formData.country,
        website: formData.website,
        hosting: formData.portNumber,
        texting_number: formData?.selectedNumber,
        type: searchType === 'tollfree' ? 'toll_free' : 'local'
      }
      if (company_id !== '' && company_id !== undefined) {
        formdata.company_id = company_id;
      }
      const response = await api.updateUserDetails(user_id, formdata);
      if (response.status === 'success') {
        toast.success("Profile completed successfully!");
        localStorage.setItem('account_id', response.data.account_id);
        localStorage.setItem('company_id', response.data.company_id);
        localStorage.setItem('company_name', response.data.company_name);
        localStorage.setItem('user_number', formData.phoneNumber);
        navigate("/pricing-plans");
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to complete setup. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setBrowsing(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setBrowsing(false);
      }
    };

    if (browsing) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [browsing, modalRef]);


  const formatPhoneNumber = (phoneNo) => {
    let numericPhoneNumber = phoneNo.replace(/\D/g, '');
    const countryCode = formData.country; // Get the selected country code

    if (countryCode === 'US' || countryCode === 'CA') {
      if (numericPhoneNumber.startsWith('+1')) {
        numericPhoneNumber = numericPhoneNumber.slice(2);
      }
      if (numericPhoneNumber.startsWith('1')) {
        numericPhoneNumber = numericPhoneNumber.slice(1);
      }
      if (numericPhoneNumber.length === 10) {
        return `+1${numericPhoneNumber}`;
      } else if (numericPhoneNumber.length === 11 && numericPhoneNumber.startsWith('1')) {
        return `+${numericPhoneNumber}`;
      }
    } else if (countryCode === 'AU') {
      if (numericPhoneNumber.startsWith('+61')) {
        numericPhoneNumber = numericPhoneNumber.slice(3);
      }
      if (numericPhoneNumber.startsWith('61')) {
        numericPhoneNumber = numericPhoneNumber.slice(2);
      }
      if (numericPhoneNumber.length === 9) {
        return `+61${numericPhoneNumber}`;
      }
    } else if (countryCode === 'UK') {
      if (numericPhoneNumber.startsWith('+44')) {
        numericPhoneNumber = numericPhoneNumber.slice(3);
      }
      if (numericPhoneNumber.startsWith('44')) {
        numericPhoneNumber = numericPhoneNumber.slice(2);
      }
      if (numericPhoneNumber.length === 10) {
        return `+44${numericPhoneNumber}`;
      }
    }
    return numericPhoneNumber; // Return unformatted if it doesn't match any criteria
  };

  const validatePhoneNumber = (phoneNo) => {
    const numericPhone = phoneNo.replace(/\D/g, '');
    const countryCode = formData.country; // Get the selected country code

    if (countryCode === 'US' || countryCode === 'CA') {
      if (numericPhone.length === 10) {
        return true;
      } else if (numericPhone.length === 11 && numericPhone.startsWith('1')) {
        return true;
      }
    } else if (countryCode === 'AU') {
      if (numericPhone.length === 9) {
        return true;
      }
    } else if (countryCode === 'GB') {
      if (numericPhone.length === 10) {
        return true;
      }
    }
    return false; // Return false if it doesn't match any criteria
  };

  if (mainLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-white p-4 sm:p-6">
      <Loader2 className="h-10 w-10 animate-spin text-gray-300" />
    </div>
  }


  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4 sm:p-6">
      <div className="w-full max-w-2xl">
        <div className="flex justify-center mb-10">
          <img
            src="https://s3.us-east-2.amazonaws.com/sc-prod-embedded.salescaptain.com/resources/2025-03-18/full_logo.a46683a620fa53c3c554_1742299090688.png"
            alt="SalesCaptain Logo"
            className="h-10 object-contain"
          />
        </div>

        <h1 className="text-2xl font-semibold text-center mb-2">
          Complete Your Profile
        </h1>
        <p className="text-gray-500 text-center mb-8">
          Tell us a bit more about yourself
        </p>

        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Row */}
            <div className="space-y-2">
              <label htmlFor="first_name" className="flex items-center text-sm font-medium">
                <User className="h-4 w-4 text-blue-600 mr-2" />
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                placeholder="Your first name"
                className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="last_name" className="flex items-center text-sm font-medium">
                <User className="h-4 w-4 text-blue-600 mr-2" />
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                placeholder="Your last name"
                className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="businessName" className="flex items-center text-sm font-medium">
                <Building className="h-4 w-4 text-blue-600 mr-2" />
                {/* Business Name <span className="text-gray-500">(optional)</span> */}
                Business Name <span className="text-red-500">*</span>
              </label>
              <input
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                placeholder="Your business name"
                className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                required
              />
            </div>

            {/* Second Row */}
            <div className="space-y-2">
              <label htmlFor="phoneNumber" className="flex items-center text-sm font-medium">
                <Phone className="h-4 w-4 text-blue-600 mr-2" />
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="Your phone number"
                  className="flex-1 h-10 px-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="website" className="flex items-center text-sm font-medium">
                <Globe className="h-4 w-4 text-blue-600 mr-2" />
                Website <span className="text-red-500">*</span>
              </label>
              <input
                id="website"
                name="website"
                type="text"
                value={formData.website}
                onChange={handleInputChange}
                onBlur={handleWebsiteBlur}
                placeholder="Your website (e.g. salescaptain.com or www.salescaptain.com)"
                className={`w-full h-10 px-3 border rounded-lg focus:outline-none ${
                  formData.website && !validateWebsiteURL(formData.website)
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-gray-200 focus:border-blue-500'
                }`}
                title="Enter your website URL (https:// will be added automatically)"
                required
              />
            </div>

            {/* <div className="space-y-2">
              <label htmlFor="country" className="flex items-center gap-2 text-sm font-medium">
                <MdLocationPin className="h-4 w-4 text-blue-600" />
                Region
              </label>
              <SelectOptions
                label="Region"
                value={formData.country}
                setValue={(value) => setFormData(prev => ({ ...prev, country: value }))}
                menuOptions={regionOptions}
                size="small"
                minWidth="100%"
              />
            </div> */}
          </div>

          <div className="py-2">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-medium">Select Number</h2>
              <button
                type="button"
                className="text-blue-600 border border-gray-200 rounded-lg px-3 py-1.5 flex items-center gap-1 text-sm hover:bg-blue-50"
                onClick={handleBrowseNumbers}
              >
                <Search className="h-4 w-4" />
                Browse Numbers
              </button>
            </div>

            <div
              className="border border-gray-200 hover:border-blue-300 rounded-lg transition-all cursor-pointer p-4"
              onClick={handleBrowseNumbers}
            >
              <div className="flex items-center justify-center flex-col">
                <p className="md:text-[30px] text-xl font-bold  text-center">{formData.selectedNumber || 'Select Number'}</p>
              </div>
            </div>

            {browsing && (
              <div ref={modalRef} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg max-w-[500px] w-full p-6">
                  <div className="flex items-center justify-between pb-2">
                    <h3 className="text-lg font-semibold">Browse Available Numbers</h3>
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-700"
                      onClick={handleCloseModal}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <p className="text-sm text-gray-500 mb-4">
                    Select a number for your business communications
                  </p>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button
                      type="button"
                      className={`py-2 px-4 rounded-lg flex items-center justify-center ${searchType === 'tollfree' ? "bg-blue-600 text-white" : "border border-gray-200"}`}
                      onClick={() => setSearchType('tollfree')}
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Toll-Free
                    </button>
                    <button
                      type="button"
                      className={`py-2 px-4 rounded-lg flex items-center justify-center ${searchType === 'local' ? "bg-blue-600 text-white" : "border border-gray-200"}`}
                      onClick={() => setSearchType('local')}
                    >
                      <Building className="h-4 w-4 mr-2" />
                      Local
                    </button>
                  </div>

                  {searchType === 'local' ? (
                    <div className="space-y-3 mb-4">
                      <div className="relative">
                        <FaLocationDot className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                        <input
                          placeholder="Search by locality (city, state, etc.)"
                          value={localitySearch}
                          onChange={(e) => setLocalitySearch(e.target.value)}
                          className="w-full h-10 pl-10 px-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div className="relative">
                        <MdDialpad className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                        <input
                          placeholder="Search by pattern (e.g. 555, 1234)"
                          value={patternSearch}
                          onChange={(e) => setPatternSearch(e.target.value)}
                          className="w-full h-10 pl-10 px-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <div className="relative">
                        <MdDialpad className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                        <input
                          placeholder="Search by pattern (e.g. 800, 888)"
                          value={patternSearch}
                          onChange={(e) => setPatternSearch(e.target.value)}
                          className="w-full h-10 pl-10  px-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                        />
                      </div>

                    </div>
                  )}
                  {miniLoading ? (
                    <div className="flex items-center justify-center h-[300px]">
                      <Loader2 size="sm" className="h-10 w-10 animate-spin text-gray-300" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 max-h-[300px] overflow-y-auto">
                      {availableNumbers.length > 0 ? availableNumbers.map((num) => (
                        <div
                          key={num.friendlyName}
                          className="border border-gray-200 rounded-lg hover:border-blue-300 cursor-pointer transition-all"
                          onClick={() => handleSelectNumber(num.phoneNumber)}
                        >
                          <div className={`p-4 flex justify-between items-center ${num.phoneNumber === formData.selectedNumber ? 'bg-blue-50 border-blue-200' : ''}`}>
                            <div>
                              <p className="text-lg font-medium">{num?.friendlyName}</p>
                            </div>
                            <button type="button" className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100">
                              <ArrowRight className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )) : (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-gray-500">No numbers found</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="pb-2">
            <div className="flex items-center justify-between p-2 border border-gray-200 rounded-lg hover:border-blue-300 transition-all">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <Upload className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-900">Want to use an existing number?</p>
                    <p className="text-sm text-gray-500">You can kick off the porting process after sign up.</p>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 ml-4">
                <Toggle
                  checked={formData.portNumber}
                  onChange={(checked) => setFormData(prev => ({ ...prev, portNumber: checked }))}
                  size="md"
                  color="blue"
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center"
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-gray-300 mr-2" />
            ) : null}
            Continue
            <ArrowRight className="ml-2 h-5 w-5" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserDetails; 